import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { profileAPI, serviceAPI, mediaAPI, pdfAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import WorkflowBuilder from '../components/WorkflowBuilder';
import { toast } from 'sonner';
import {
  Zap,
  ArrowLeft,
  Save,
  Trash2,
  Plus,
  Image,
  Video,
  Link2,
  Phone,
  Mail,
  Globe,
  User,
  Briefcase,
  Eye,
  X,
  GitBranch,
  FileText,
  Upload
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const PROFILE_COLORS = {
  green: { bg: 'bg-emerald-500', hex: '#22C55E', name: 'Green' },
  blue: { bg: 'bg-blue-500', hex: '#3B82F6', name: 'Blue' },
  red: { bg: 'bg-red-500', hex: '#EF4444', name: 'Red' },
  yellow: { bg: 'bg-yellow-500', hex: '#EAB308', name: 'Yellow' },
  purple: { bg: 'bg-purple-500', hex: '#A855F7', name: 'Purple' },
  black: { bg: 'bg-slate-900', hex: '#171717', name: 'Black' },
};

const ProfileEditor = () => {
  const { profileId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [profile, setProfile] = useState(null);
  const [services, setServices] = useState([]);
  const [media, setMedia] = useState({ videos: [], photos: [] });
  const [pdfs, setPdfs] = useState([]);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    profile_name: '',
    title: '',
    bio: '',
    phone: '',
    email: '',
    website: '',
  });

  const [profilePhotoUrl, setProfilePhotoUrl] = useState(null);
  const [profileLogoUrl, setProfileLogoUrl] = useState(null);
  const [photoUploading2, setPhotoUploading2] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [newService, setNewService] = useState({ service_name: '', description: '', price: '' });
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [contactMode, setContactMode] = useState('full');
  const [contactVisibility, setContactVisibility] = useState({
    phone: true, email: true, whatsapp: true, viber: true, vcf: true, website: true, services: true
  });
  const [sectionVisibility, setSectionVisibility] = useState({
    header: true, contact: true, services: true, photos: true, videos: true, pdfs: true, lead_form: true, cta: true
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [profileRes, servicesRes, mediaRes, pdfsRes] = await Promise.all([
          profileAPI.get(profileId),
          serviceAPI.list(profileId),
          mediaAPI.get(profileId),
          pdfAPI.list(profileId),
        ]);
        
        setProfile(profileRes.data);
        setServices(servicesRes.data);
        setMedia(mediaRes.data);
        setPdfs(pdfsRes.data);
        setFormData({
          profile_name: profileRes.data.profile_name || '',
          title: profileRes.data.title || '',
          bio: profileRes.data.bio || '',
          phone: profileRes.data.phone || '',
          email: profileRes.data.email || '',
          website: profileRes.data.website || '',
        });
        setProfilePhotoUrl(profileRes.data.profile_photo_url || null);
        setProfileLogoUrl(profileRes.data.profile_logo_url || null);
        setContactMode(profileRes.data.contact_mode || 'full');
        setContactVisibility(profileRes.data.contact_visibility || {
          phone: true, email: true, whatsapp: true, viber: true, vcf: true, website: true, services: true
        });
        setSectionVisibility(profileRes.data.section_visibility || {
          header: true, contact: true, services: true, photos: true, videos: true, pdfs: true, lead_form: true, cta: true
        });
      } catch (error) {
        toast.error('Failed to load profile');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [profileId, navigate]);

  const handleSave = async () => {
    // Validate at least one content section is visible
    const contentKeys = ['videos', 'photos', 'pdfs', 'lead_form', 'cta'];
    const hasVisibleContent = contentKeys.some(k => sectionVisibility[k]);
    if (!hasVisibleContent) {
      toast.error('At least one section must be visible.');
      return;
    }
    setSaving(true);
    try {
      await profileAPI.update(profileId, {
        ...formData,
        contact_mode: contactMode,
        contact_visibility: contactVisibility,
        section_visibility: sectionVisibility,
      });
      toast.success('Profile saved!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this profile?')) return;
    
    try {
      await profileAPI.delete(profileId);
      toast.success('Profile deleted');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to delete profile');
    }
  };

  const handleAddService = async () => {
    if (!newService.service_name) {
      toast.error('Service name is required');
      return;
    }
    
    try {
      const response = await serviceAPI.create(profileId, newService);
      setServices([...services, response.data]);
      setNewService({ service_name: '', description: '', price: '' });
      setShowServiceForm(false);
      toast.success('Service added!');
    } catch (error) {
      toast.error('Failed to add service');
    }
  };

  const handleDeleteService = async (serviceId) => {
    try {
      await serviceAPI.delete(profileId, serviceId);
      setServices(services.filter(s => s.service_id !== serviceId));
      toast.success('Service removed');
    } catch (error) {
      toast.error('Failed to remove service');
    }
  };

  const handleUploadVideo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ['video/mp4', 'video/webm', 'video/quicktime'];
    if (!allowed.includes(file.type)) { toast.error('Only MP4, WebM, or QuickTime files allowed'); e.target.value = ''; return; }
    if (file.size > 100 * 1024 * 1024) { toast.error('Video must be under 100MB. Tip: Upload MP4 720p for best results.'); e.target.value = ''; return; }
    setVideoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', file.name);
      const response = await mediaAPI.uploadVideo(profileId, fd);
      setMedia({ ...media, videos: [...media.videos, response.data] });
      toast.success('Video uploaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload video');
    } finally { setVideoUploading(false); e.target.value = ''; }
  };

  const handleUploadPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) { toast.error('Only JPEG, PNG, or WebP files allowed'); e.target.value = ''; return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Photo must be under 10MB'); e.target.value = ''; return; }
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('caption', file.name);
      const response = await mediaAPI.uploadPhoto(profileId, fd);
      setMedia({ ...media, photos: [...media.photos, response.data] });
      toast.success('Photo uploaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally { setPhotoUploading(false); e.target.value = ''; }
  };

  const handleDeleteMedia = async (mediaId, type) => {
    try {
      await mediaAPI.delete(profileId, mediaId);
      if (type === 'video') {
        setMedia({ ...media, videos: media.videos.filter(v => v.id !== mediaId) });
      } else {
        setMedia({ ...media, photos: media.photos.filter(p => p.id !== mediaId) });
      }
      toast.success('Media removed');
    } catch (error) {
      toast.error('Failed to remove media');
    }
  };

  const handleUploadPdf = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are allowed');
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error('File size must be under 25MB');
      return;
    }
    setPdfUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', file.name.replace('.pdf', ''));
      formData.append('description', '');
      const response = await pdfAPI.upload(profileId, formData);
      setPdfs([...pdfs, response.data]);
      toast.success('PDF uploaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload PDF');
    } finally {
      setPdfUploading(false);
      e.target.value = '';
    }
  };

  const handleDeletePdf = async (pdfId) => {
    try {
      await pdfAPI.delete(pdfId);
      setPdfs(pdfs.filter(p => p.id !== pdfId));
      toast.success('PDF removed');
    } catch (error) {
      toast.error('Failed to remove PDF');
    }
  };

  const handleUploadProfilePhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) { toast.error('Only JPEG, PNG, or WebP files allowed'); e.target.value = ''; return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Profile photo must be under 5MB'); e.target.value = ''; return; }
    setPhotoUploading2(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await mediaAPI.uploadProfilePhoto(profileId, fd);
      setProfilePhotoUrl(res.data.profile_photo_url);
      toast.success('Profile photo uploaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload profile photo');
    } finally { setPhotoUploading2(false); e.target.value = ''; }
  };

  const handleRemoveProfilePhoto = async () => {
    try {
      await mediaAPI.deleteProfilePhoto(profileId);
      setProfilePhotoUrl(null);
      toast.success('Profile photo removed');
    } catch (error) {
      toast.error('Failed to remove profile photo');
    }
  };

  const handleUploadLogo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'];
    if (!allowed.includes(file.type)) { toast.error('Only JPEG, PNG, WebP, or SVG files allowed'); e.target.value = ''; return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Logo must be under 5MB'); e.target.value = ''; return; }
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await mediaAPI.uploadLogo(profileId, fd);
      setProfileLogoUrl(res.data.profile_logo_url);
      toast.success('Logo uploaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally { setLogoUploading(false); e.target.value = ''; }
  };

  const handleRemoveLogo = async () => {
    try {
      await mediaAPI.deleteLogo(profileId);
      setProfileLogoUrl(null);
      toast.success('Logo removed');
    } catch (error) {
      toast.error('Failed to remove logo');
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  const colorStyles = PROFILE_COLORS[profile?.color] || PROFILE_COLORS.green;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top Bar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="p-2 text-slate-600 hover:text-slate-900">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 ${colorStyles.bg} rounded-lg`} />
                <h1 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Edit {colorStyles.name} Profile
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to={`/p/${profileId}`}
                target="_blank"
                className="hidden sm:flex items-center gap-2 text-slate-600 hover:text-slate-900 text-sm"
              >
                <Eye className="w-4 h-4" />
                Preview
              </Link>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="save-profile-btn"
              >
                {saving ? <div className="spinner w-4 h-4 border-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-slate-100 rounded-lg p-1 max-w-md" data-testid="profile-tabs">
          <button
            onClick={() => setActiveTab('details')}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'details' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid="tab-details"
          >
            <User className="w-4 h-4" />
            Details
          </button>
          <button
            onClick={() => setActiveTab('visibility')}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'visibility' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid="tab-visibility"
          >
            <Eye className="w-4 h-4" />
            Visibility
          </button>
          <button
            onClick={() => setActiveTab('workflow')}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'workflow' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid="tab-workflow"
          >
            <GitBranch className="w-4 h-4" />
            Workflow
          </button>
        </div>

        {activeTab === 'workflow' ? (
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <WorkflowBuilder profileId={profileId} videos={media.videos} pdfs={pdfs} />
          </div>
        ) : activeTab === 'visibility' ? (
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Profile Visibility</h2>
              <p className="text-sm text-slate-500">Control what visitors can see on your public profile</p>
            </div>

            {/* Preset Modes */}
            <div data-testid="contact-mode-selector">
              <Label className="text-sm font-medium text-slate-700 mb-2 block">Preset Mode</Label>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                {[
                  { value: 'full', label: 'Full Contact', desc: 'All info visible' },
                  { value: 'campaign', label: 'Campaign', desc: 'Hide contact + VCF' },
                  { value: 'gated', label: 'Gated Contact', desc: 'Unlock after lead' },
                  { value: 'broadcast', label: 'Broadcast', desc: 'Video + CTA only' },
                ].map(mode => (
                  <button key={mode.value} onClick={() => {
                    setContactMode(mode.value);
                    if (mode.value === 'full') {
                      setContactVisibility({ phone: true, email: true, whatsapp: true, viber: true, vcf: true, website: true, services: true });
                      setSectionVisibility({ header: true, contact: true, services: true, photos: true, videos: true, pdfs: true, lead_form: true, cta: true });
                    }
                    if (mode.value === 'campaign') {
                      setContactVisibility({ phone: false, email: false, whatsapp: false, viber: false, vcf: false, website: false, services: true });
                      setSectionVisibility({ header: true, contact: false, services: true, photos: true, videos: true, pdfs: true, lead_form: true, cta: true });
                    }
                    if (mode.value === 'broadcast') {
                      setContactVisibility({ phone: false, email: false, whatsapp: false, viber: false, vcf: false, website: false, services: false });
                      setSectionVisibility({ header: false, contact: false, services: false, photos: false, videos: true, pdfs: false, lead_form: false, cta: true });
                    }
                  }}
                    className={`p-3 rounded-xl border text-left transition-colors ${contactMode === mode.value ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-slate-300'}`}
                    data-testid={`mode-${mode.value}`}
                  >
                    <p className="text-sm font-medium text-slate-900">{mode.label}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{mode.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Section Visibility */}
            <div data-testid="section-visibility-toggles">
              <Label className="text-sm font-medium text-slate-700 mb-3 block">Section Visibility</Label>
              <div className="space-y-3">
                {[
                  { key: 'header', label: 'Header (name, title, photo, logo)' },
                  { key: 'contact', label: 'Contact Section (buttons, VCF)' },
                  { key: 'services', label: 'Services Section' },
                  { key: 'photos', label: 'Photos Section' },
                  { key: 'videos', label: 'Videos Section' },
                  { key: 'pdfs', label: 'PDFs / Downloads Section' },
                  { key: 'lead_form', label: 'Lead Form' },
                  { key: 'cta', label: 'CTA Section' },
                ].map(item => (
                  <div key={item.key} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                    <span className="text-sm text-slate-700">{item.label}</span>
                    <button
                      onClick={() => {
                        const updated = { ...sectionVisibility, [item.key]: !sectionVisibility[item.key] };
                        const contentKeys = ['videos', 'photos', 'pdfs', 'lead_form', 'cta'];
                        const hasContent = contentKeys.some(k => updated[k]);
                        if (!hasContent) {
                          toast.error('At least one section must be visible.');
                          return;
                        }
                        setSectionVisibility(updated);
                      }}
                      className={`w-10 h-6 rounded-full transition-colors ${sectionVisibility[item.key] ? 'bg-indigo-600' : 'bg-slate-300'}`}
                      data-testid={`section-toggle-${item.key}`}
                    >
                      <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform mx-1 ${sectionVisibility[item.key] ? 'translate-x-4' : ''}`} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact Toggles (shown when contact section is visible) */}
            {sectionVisibility.contact && (
              <div data-testid="visibility-toggles">
                <Label className="text-sm font-medium text-slate-700 mb-3 block">Contact Detail Controls</Label>
                <div className="space-y-3">
                  {[
                    { key: 'phone', label: 'Phone Number' },
                    { key: 'email', label: 'Email Address' },
                    { key: 'whatsapp', label: 'WhatsApp' },
                    { key: 'viber', label: 'Viber' },
                    { key: 'vcf', label: 'Save Contact (VCF)' },
                    { key: 'website', label: 'Website Link' },
                    { key: 'services', label: 'Services in Contact' },
                  ].map(item => (
                    <div key={item.key} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                      <span className="text-sm text-slate-700">{item.label}</span>
                      <button
                        onClick={() => setContactVisibility({ ...contactVisibility, [item.key]: !contactVisibility[item.key] })}
                        className={`w-10 h-6 rounded-full transition-colors ${contactVisibility[item.key] ? 'bg-indigo-600' : 'bg-slate-300'}`}
                        data-testid={`toggle-${item.key}`}
                      >
                        <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform mx-1 ${contactVisibility[item.key] ? 'translate-x-4' : ''}`} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {contactMode === 'gated' && (
              <div className="bg-amber-50 rounded-lg p-3 text-sm text-amber-700">
                <p className="font-medium">Gated Contact Mode</p>
                <p className="mt-0.5">Contact buttons will be hidden until the visitor submits a lead form. Once submitted, contacts unlock for the session.</p>
              </div>
            )}

            {contactMode === 'broadcast' && (
              <div className="bg-indigo-50 rounded-lg p-3 text-sm text-indigo-700">
                <p className="font-medium">Broadcast Mode</p>
                <p className="mt-0.5">Public page will show only Video + CTA. Ideal for campaign landing pages. Analytics still tracked (taps, views, video plays, CTA clicks).</p>
              </div>
            )}

            <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-visibility-btn">
              {saving ? <div className="spinner w-4 h-4 border-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Visibility Settings
            </Button>
          </div>
        ) : (
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Basic Information
              </h2>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="profile_name">Profile Name</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="profile_name"
                      className="pl-10"
                      value={formData.profile_name}
                      onChange={(e) => setFormData({ ...formData, profile_name: e.target.value })}
                      placeholder="e.g., Sales Manager"
                      data-testid="profile-name-input"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="title">Title / Role</Label>
                  <div className="relative mt-1">
                    <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="title"
                      className="pl-10"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="e.g., Senior Sales Executive"
                      data-testid="title-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="bio">Bio</Label>
                  <Textarea
                    id="bio"
                    className="mt-1"
                    value={formData.bio}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    placeholder="Tell people about yourself..."
                    rows={3}
                    data-testid="bio-input"
                  />
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Contact Information
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="phone"
                      type="tel"
                      className="pl-10"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+1 234 567 890"
                      data-testid="phone-input"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="email">Email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      className="pl-10"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="you@example.com"
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <Label htmlFor="website">Website</Label>
                  <div className="relative mt-1">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="website"
                      type="url"
                      className="pl-10"
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      placeholder="https://yourwebsite.com"
                      data-testid="website-input"
                    />
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <Label>Profile Photo</Label>
                  <div className="mt-2 flex items-center gap-4">
                    <div className={`w-16 h-16 rounded-full overflow-hidden flex-shrink-0 ${colorStyles.bg} flex items-center justify-center`}>
                      {profilePhotoUrl ? (
                        <img src={profilePhotoUrl.startsWith('/api') ? `${API_BASE}${profilePhotoUrl}` : profilePhotoUrl} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-white text-xl font-bold">{formData.profile_name?.charAt(0)?.toUpperCase() || '?'}</span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <label className="cursor-pointer">
                        <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleUploadProfilePhoto} disabled={photoUploading2} data-testid="profile-photo-input" />
                        <span className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${photoUploading2 ? 'text-slate-400 bg-slate-100 border-slate-200' : 'text-indigo-600 hover:bg-indigo-50 border-indigo-200'}`}>
                          {photoUploading2 ? <><div className="spinner w-3 h-3 border" /> Uploading...</> : <><Upload className="w-4 h-4" /> {profilePhotoUrl ? 'Change' : 'Upload'}</>}
                        </span>
                      </label>
                      {profilePhotoUrl && (
                        <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={handleRemoveProfilePhoto} data-testid="remove-profile-photo-btn">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">JPEG, PNG, or WebP. Max 5MB.</p>
                </div>

                <div className="sm:col-span-2">
                  <Label>Logo</Label>
                  <div className="mt-2 flex items-center gap-4">
                    <div className="w-16 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-slate-100 flex items-center justify-center border border-slate-200">
                      {profileLogoUrl ? (
                        <img src={profileLogoUrl.startsWith('/api') ? `${API_BASE}${profileLogoUrl}` : profileLogoUrl} alt="Logo" className="max-w-full max-h-full object-contain" />
                      ) : (
                        <Briefcase className="w-5 h-5 text-slate-300" />
                      )}
                    </div>
                    <div className="flex gap-2">
                      <label className="cursor-pointer">
                        <input type="file" accept="image/jpeg,image/png,image/webp,image/svg+xml" className="hidden" onChange={handleUploadLogo} disabled={logoUploading} data-testid="logo-input" />
                        <span className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${logoUploading ? 'text-slate-400 bg-slate-100 border-slate-200' : 'text-indigo-600 hover:bg-indigo-50 border-indigo-200'}`}>
                          {logoUploading ? <><div className="spinner w-3 h-3 border" /> Uploading...</> : <><Upload className="w-4 h-4" /> {profileLogoUrl ? 'Change' : 'Upload'}</>}
                        </span>
                      </label>
                      {profileLogoUrl && (
                        <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={handleRemoveLogo} data-testid="remove-logo-btn">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">JPEG, PNG, WebP, or SVG. Max 5MB.</p>
                </div>
              </div>
            </div>

            {/* Services */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Services Offered
                </h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowServiceForm(true)}
                  data-testid="add-service-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Service
                </Button>
              </div>

              {showServiceForm && (
                <div className="bg-slate-50 rounded-lg p-4 mb-4">
                  <div className="space-y-3">
                    <Input
                      placeholder="Service name"
                      value={newService.service_name}
                      onChange={(e) => setNewService({ ...newService, service_name: e.target.value })}
                      data-testid="new-service-name"
                    />
                    <Input
                      placeholder="Description (optional)"
                      value={newService.description}
                      onChange={(e) => setNewService({ ...newService, description: e.target.value })}
                    />
                    <Input
                      placeholder="Price (optional)"
                      value={newService.price}
                      onChange={(e) => setNewService({ ...newService, price: e.target.value })}
                    />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleAddService} className="bg-indigo-600 hover:bg-indigo-700">
                        Add
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setShowServiceForm(false)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {services.length === 0 ? (
                <p className="text-slate-500 text-sm">No services added yet</p>
              ) : (
                <div className="space-y-2">
                  {services.map((service) => (
                    <div key={service.service_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium text-slate-900">{service.service_name}</p>
                        {service.description && <p className="text-sm text-slate-500">{service.description}</p>}
                      </div>
                      <div className="flex items-center gap-3">
                        {service.price && <span className="text-sm font-medium text-slate-600">{service.price}</span>}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteService(service.service_id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Media */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Media
              </h2>

              {/* Videos */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-slate-700">Videos ({media.videos.length}/3)</h3>
                  {media.videos.length < 3 && (
                    <label className="cursor-pointer">
                      <input type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={handleUploadVideo} disabled={videoUploading} data-testid="video-file-input" />
                      <span className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${videoUploading ? 'text-slate-400 bg-slate-100' : 'text-indigo-600 hover:bg-indigo-50'}`}>
                        {videoUploading ? <><div className="spinner w-3 h-3 border" /> Uploading...</> : <><Upload className="w-4 h-4" /> Upload Video</>}
                      </span>
                    </label>
                  )}
                </div>
                <p className="text-xs text-slate-400 mb-3">MP4, WebM, or QuickTime. Max 100MB. Tip: Upload MP4 720p for best results.</p>
                <div className="grid grid-cols-3 gap-3">
                  {media.videos.map((video) => (
                    <div key={video.id} className="relative aspect-video bg-slate-100 rounded-lg overflow-hidden group" data-testid={`video-${video.id}`}>
                      <video src={`${API_BASE}${video.url}`} className="w-full h-full object-cover" />
                      <button
                        onClick={() => handleDeleteMedia(video.id, 'video')}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`delete-video-${video.id}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                      {video.title && (
                        <p className="absolute bottom-2 left-2 text-xs text-slate-600 bg-white/80 px-2 py-0.5 rounded truncate max-w-[80%]">
                          {video.title}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Photos */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-slate-700">Photos ({media.photos.length}/3)</h3>
                  {media.photos.length < 3 && (
                    <label className="cursor-pointer">
                      <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleUploadPhoto} disabled={photoUploading} data-testid="photo-file-input" />
                      <span className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${photoUploading ? 'text-slate-400 bg-slate-100' : 'text-indigo-600 hover:bg-indigo-50'}`}>
                        {photoUploading ? <><div className="spinner w-3 h-3 border" /> Uploading...</> : <><Upload className="w-4 h-4" /> Upload Photo</>}
                      </span>
                    </label>
                  )}
                </div>
                <p className="text-xs text-slate-400 mb-3">JPEG, PNG, or WebP. Max 10MB.</p>
                <div className="grid grid-cols-3 gap-3">
                  {media.photos.map((photo) => (
                    <div key={photo.id} className="relative aspect-square bg-slate-100 rounded-lg overflow-hidden group" data-testid={`photo-${photo.id}`}>
                      <img src={`${API_BASE}${photo.url}`} alt={photo.title} className="w-full h-full object-cover" />
                      <button
                        onClick={() => handleDeleteMedia(photo.id, 'photo')}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`delete-photo-${photo.id}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* PDFs */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-slate-700">PDFs ({pdfs.length}/3)</h3>
                {pdfs.length < 3 && (
                  <label className="cursor-pointer">
                    <input type="file" accept=".pdf" className="hidden" onChange={handleUploadPdf} disabled={pdfUploading} data-testid="pdf-file-input" />
                    <span className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-700 font-medium">
                      {pdfUploading ? <div className="spinner w-4 h-4 border-2" /> : <Upload className="w-4 h-4" />}
                      Upload PDF
                    </span>
                  </label>
                )}
              </div>
              <div className="space-y-2">
                {pdfs.map((pdf) => (
                  <div key={pdf.id} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100" data-testid={`pdf-item-${pdf.id}`}>
                    <FileText className="w-8 h-8 text-red-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{pdf.title}</p>
                      <p className="text-xs text-slate-500">{(pdf.file_size / 1024).toFixed(0)} KB</p>
                    </div>
                    <button onClick={() => handleDeletePdf(pdf.id)} className="p-1 text-slate-400 hover:text-red-500" data-testid={`delete-pdf-${pdf.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {pdfs.length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-4">No PDFs uploaded yet</p>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Preview Card */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="text-sm font-medium text-slate-700 mb-4">Preview</h3>
              <div className="text-center">
                <div className={`w-20 h-20 ${colorStyles.bg} rounded-full mx-auto mb-3 flex items-center justify-center overflow-hidden`}>
                  {profilePhotoUrl ? (
                    <img
                      src={profilePhotoUrl.startsWith('/api') ? `${API_BASE}${profilePhotoUrl}` : profilePhotoUrl}
                      alt={formData.profile_name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-white text-2xl font-bold">
                      {formData.profile_name?.charAt(0).toUpperCase() || '?'}
                    </span>
                  )}
                </div>
                {profileLogoUrl && (
                  <img src={profileLogoUrl.startsWith('/api') ? `${API_BASE}${profileLogoUrl}` : profileLogoUrl} alt="Logo" className="h-6 mx-auto mb-2 object-contain" />
                )}
                <p className="font-semibold text-slate-900">{formData.profile_name || 'Profile Name'}</p>
                <p className="text-sm text-slate-500">{formData.title || 'Title'}</p>
              </div>
            </div>

            {/* Profile Link */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="text-sm font-medium text-slate-700 mb-3">Share Link</h3>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-slate-50 rounded-lg p-3 text-sm text-slate-600 truncate">
                  {window.location.origin}/p/{profileId}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/p/${profileId}`);
                    toast.success('Link copied!');
                  }}
                  data-testid="copy-link-btn"
                >
                  <Link2 className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Danger Zone */}
            <div className="bg-white rounded-xl border border-red-200 p-6">
              <h3 className="text-sm font-medium text-red-700 mb-3">Danger Zone</h3>
              <Button
                variant="outline"
                className="w-full text-red-600 border-red-200 hover:bg-red-50"
                onClick={handleDelete}
                data-testid="delete-profile-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Profile
              </Button>
            </div>
          </div>
        </div>
        )}
      </main>
    </div>
  );
};

export default ProfileEditor;
